import { memo } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { useMatchMedia, Text, HStack } from '@library';
import type { ProductItem, ProductWithRelatedData } from '../../types';
import ProductTilePricingAndActions from '../ProductTileView/ProductTilePricingAndActions';
import ProductTileAvailability from '../ProductTileView/ProductTileAvailability';
import ProductTileTag from '../ProductTileView/ProductTileTag';
import useTabletView from '../../../core/hooks/dom/useTabletView';
import ProductTileCarousel from '../ProductTileView/ProductTileCarousel';
import useProductView from '../../hooks/useProductView';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import ProductListViewAttributes from './ProductListViewAttributes';
import ProductListViewSummary from './ProductListViewSummary';
import styles from './ProductListView.module.scss';

const ProductImageContent = memo(
    ({
        product,
        productDetailsLink,
    }: {
        product: ProductItem;
        productDetailsLink: string;
    }) => {
        const { manufacturer } = product;
        return (
            <div className={styles.productImageContainer}>
                <div className={styles.productTag}>
                    <ProductTileTag product={product} />
                </div>
                <div className={styles.productImageCarousel}>
                    <ProductTileCarousel
                        productDetailsLink={productDetailsLink}
                        media={product.media}
                    />
                </div>

                <div className={styles.manufactureBlock}>
                    <Text tertiary medium uppercase center>
                        <span className={styles.manufacturer}>
                            {manufacturer}
                        </span>
                    </Text>
                </div>
            </div>
        );
    }
);

interface ProductListViewProps {
    product: ProductWithRelatedData;
    renderInstantly?: boolean;
    loading?: boolean;
}

const ProductListDetails = memo(
    ({
        product,
        productDetailsLink,
        activeSKU,
        setActiveSKU,
    }: {
        product: ProductWithRelatedData;
        productDetailsLink: string;
        activeSKU?: string;
        setActiveSKU(sku: string): void;
    }) => {
        const isLargeView = useMatchMedia('(min-width: 1366px)');
        const isTabletView = useTabletView();

        const { variants } = product;
        return (
            <div className={styles.productListDetails}>
                <ProductImageContent
                    product={product}
                    productDetailsLink={productDetailsLink}
                />
                <div className={styles.productDetailsBody}>
                    <div className={styles.availability}>
                        <ProductTileAvailability
                            persistMessage={isLargeView}
                            hasVariants={variants.length > 0}
                            skuList={product.variants.map(item => item.sku)}
                            activeSKU={activeSKU}
                        />
                    </div>
                    <div className={styles.productHeader}>
                        <div className={styles.activeSku}>
                            <HStack>
                                <Text
                                    secondary={isTabletView}
                                    tertiary={!isTabletView}
                                    medium
                                >
                                    <FormattedMessage id="Common ItemNumber" />
                                    :&nbsp;
                                </Text>
                                <Text
                                    secondary={isTabletView}
                                    tertiary={!isTabletView}
                                    uppercase={!!activeSKU}
                                >
                                    {activeSKU || (
                                        <FormattedMessage id="please make a choice" />
                                    )}
                                </Text>
                            </HStack>
                        </div>
                    </div>
                    <div className={styles.productInfo}>
                        <div className={styles.productSummary}>
                            <ProductListViewSummary
                                productDetailsLink={productDetailsLink}
                                product={product}
                                activeSKU={activeSKU}
                                setActiveSKU={setActiveSKU}
                            />
                        </div>
                        <div className={styles.productAttributes}>
                            <ProductListViewAttributes
                                shortView={!isLargeView}
                                activeSku={activeSKU}
                                product={product}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

const ProductListView = memo(
    ({
        product,
        loading = false,
        renderInstantly = false,
        ...ariaProps
    }: ProductListViewProps) => {
        const {
            isShow,
            ref,
            activeSKU,
            setActiveSKU,
            productDetailsLink,
            customerPrice,
            startingPrice,
        } = useProductView({
            product,
            renderInstantly,
        });

        return (
            <div
                className={clsx(styles.productListView, {
                    [styles.disableInteractions]: loading,
                })}
                ref={ref}
                {...ariaProps}
            >
                {isShow && (
                    <HStack align="stretch">
                        <ProductListDetails
                            product={product}
                            productDetailsLink={productDetailsLink}
                            activeSKU={activeSKU}
                            setActiveSKU={setActiveSKU}
                        />

                        <div className={styles.pricingAndActions}>
                            <ProductTilePricingAndActions
                                product={product}
                                selectedSKU={activeSKU}
                                customerPrice={customerPrice}
                                variantStartingPrice={startingPrice}
                            />
                        </div>
                    </HStack>
                )}
            </div>
        );
    }
);

export default memo(
    withErrorBoundary(ProductListView, {
        customMessage: <FormattedMessage id="Product can't be displayed" />,
    })
);
